/**
 * Centralized Route Manager Component
 * 
 * This component handles all route middleware logic in one place,
 * eliminating the need for scattered middleware implementations.
 */

import React from 'react';
import { Routes, Route, Navigate, useParams, useSearchParams } from 'react-router-dom';
import RouteMiddleware from './RouteMiddleware';
import { ROUTES, REDIRECT_ROUTES, getAllRoutes } from '../../config/routes';

// Legacy marketplace category URLs (/marketplace/category/:uid) are unified
// into the PLP search route: the uid becomes the multi-select category_uid
// query param, other params (e.g. ?name=) are carried over.
const MarketplaceCategoryRedirect = () => {
  const { uid } = useParams();
  const [searchParams] = useSearchParams();
  const next = new URLSearchParams(searchParams);
  if (uid) next.set('category_uid', uid);
  const query = next.toString();
  return <Navigate to={`/marketplace/search${query ? `?${query}` : ''}`} replace />;
};

// Placeholder components for routes that don't exist yet
const EmergencyPage = () => (
  <div
    style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontFamily: '"Red Hat Display", sans-serif',
    }}
  >
    <div
      style={{
        textAlign: "center",
        padding: "40px",
        background: "#FFFFFF",
        borderRadius: "16px",
        boxShadow: "0 8px 25px rgba(0, 0, 0, 0.1)",
      }}
    >
      <h1 style={{ color: "#1F2937", marginBottom: "16px" }}>
        Emergency Submission
      </h1>
      <p style={{ color: "#6B7280", marginBottom: "24px" }}>
        This page is coming soon!
      </p>
      <button
        onClick={() => window.history.back()}
        style={{
          padding: "12px 24px",
          background: "#EF4444",
          color: "#FFFFFF",
          border: "none",
          borderRadius: "12px",
          fontFamily: '"Red Hat Display", sans-serif',
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        Go Back
      </button>
    </div>
  </div>
);

const CreatePostPage = () => (
  <div
    style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontFamily: '"Red Hat Display", sans-serif',
    }}
  >
    <div
      style={{
        textAlign: "center",
        padding: "40px",
        background: "#FFFFFF",
        borderRadius: "16px",
        boxShadow: "0 8px 25px rgba(0, 0, 0, 0.1)",
      }}
    >
      <h1 style={{ color: "#1F2937", marginBottom: "16px" }}>Create Post</h1>
      <p style={{ color: "#6B7280", marginBottom: "24px" }}>
        Share your pet's joy with the community!
      </p>
      <button
        onClick={() => window.history.back()}
        style={{
          padding: "12px 24px",
          background: "#8548A8",
          color: "#FFFFFF",
          border: "none",
          borderRadius: "12px",
          fontFamily: '"Red Hat Display", sans-serif',
          fontWeight: 600,
          cursor: "pointer",
        }}
      >
        Go Back
      </button>
    </div>
  </div>
);

/**
 * RouteManager Component
 * 
 * Centralized component that renders all routes with their middleware requirements.
 * This eliminates the need for scattered middleware logic across the application.
 */
const RouteManager = () => {
  // Get all routes from the single centralized configuration
  const allRoutes = getAllRoutes();

  // Render a route with middleware
  const renderRoute = (routeConfig) => {
    const { path, component: Component, middleware } = routeConfig;

    // If no middleware requirements, render component directly (public routes)
    if (!middleware || !middleware.requireAuth) {
      return <Route key={path} path={path} element={<Component />} />;
    }

    // Render authenticated routes with middleware protection (layout wraps all routes)
    return (
      <Route
        key={path}
        path={path}
        element={
          <RouteMiddleware
            allowedUserTypes={middleware.allowedUserTypes}
            requireAuth={middleware.requireAuth}
            fallbackPath={middleware.fallbackPath}
            minScreenWidth={middleware.minScreenWidth}
          >
            <Component />
          </RouteMiddleware>
        }
      />
    );
  };

  return (
    <Routes>
      {/* Render all configured routes from the single ROUTES object */}
      {allRoutes.map(renderRoute)}

      {/* Handle redirect routes */}
      {Object.entries(REDIRECT_ROUTES).map(([from, to]) => (
        <Route
          key={from}
          path={from}
          element={<Navigate to={to} replace />}
        />
      ))}

      {/* Additional routes that don't fit the standard pattern */}
      <Route path="/emergency" element={<EmergencyPage />} />
      <Route path="/social/create-post" element={<CreatePostPage />} />
      
      {/* Legacy routes for backward compatibility */}
      {/* <Route path="/pets/add/" element={<Navigate to="/pets/add" replace />} /> */}
      <Route path="/pets/" element={<Navigate to="/dashboard/pets" replace />} />
      <Route path="/pets/:petId" element={<Navigate to="/pets/:petId/view" replace />} />
      <Route path="/test-pets" element={<Navigate to="/dashboard/pets" replace />} />
      {/* <Route path="/dev/add-pet" element={<Navigate to="/pets/add" replace />} /> */}
      {/* MARKETPLACE-HIDDEN: restore with the marketplace routes (routes.js). */}
      {/* <Route path="/marketplace/category/:uid" element={<MarketplaceCategoryRedirect />} /> */}

      {/* Catch-all route */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default RouteManager;
